import { Component, computed, effect, Signal, inject } from '@angular/core'
import { OfferFormService } from '../../shared/services/offer-form.service'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { ProductionTechnology } from '../../shared/models/production-technology.enum'
import { BricksCardComponent, BricksErrorComponent, BricksIconComponent, BricksInputComponent } from '@enbw_bricks/ui'
import { TooltipComponent } from '../../shared/components/tooltip/tooltip.component'
import { toSignal } from '@angular/core/rxjs-interop'
import { RequiredAsteriskComponent } from '../../shared/components/required-asterisk/required-asterisk.component'
import { BatteryStorageStatus, BatteryStorageUsage } from '../../shared/models/battery-storage-status.enum'
import { MatDialog } from '@angular/material/dialog'
import { BatteryUsageHintComponent } from './battery-usage-hint/battery-usage-hint.component'
import { filter, pairwise, startWith } from 'rxjs'
import { GeneralFormControls } from '../../shared/models/main-form'
import { MathService } from '../../shared/util/math.service'
import { CustomValidators } from '../../shared/validators/custom-validators'
import { DigitsOnlyDirective } from '../../shared/directives/digits-only.directive'
import { FormatViewDirective } from '../../shared/directives/format-view.directive'
import { ViewFormatterService } from '../../shared/services/view-formatter.service'
import { environment } from '../../../environments/environment'

@Component({
  selector: 'app-consumption-info',
  imports: [
    BricksInputComponent,
    BricksCardComponent,
    BricksErrorComponent,
    TooltipComponent,
    ReactiveFormsModule,
    RequiredAsteriskComponent,
    DigitsOnlyDirective,
    BricksIconComponent,
    FormatViewDirective,
  ],
  templateUrl: './consumption-info.component.html',
  styleUrl: './consumption-info.component.scss',
})
export class ConsumptionInfoComponent {
  protected readonly enableSmallDV = environment.enableSmallDV
  private minTotalInstalledCapacityForSolar = environment.enableSmallDV ? 5000 : 5000.001
  readonly offerFormService = inject(OfferFormService)
  private readonly dialog = inject(MatDialog)

  protected readonly batteryStorageOptions = [
    {
      value: BatteryStorageStatus.Exists,
      label: 'Ja, ein Batterie-Speicher ist vorhanden.',
    },
    {
      value: BatteryStorageStatus.Planned,
      label: 'Ja, ein Batterie-Speicher ist geplant.',
    },
    {
      value: BatteryStorageStatus.NotPlanned,
      label: 'Nein, ein Batterie-Speicher ist weder vorhanden noch geplant.',
    },
  ]

  protected readonly batteryUsageOptions = [
    {
      value: BatteryStorageUsage.Optimization,
      label: 'Eigenverbrauchsoptimierung',
    },
    {
      value: BatteryStorageUsage.PeakShaving,
      label: 'Peak-Shaving / Lastspitzenkappung',
    },
    {
      value: BatteryStorageUsage.AtypicalUsage,
      label: 'Atypische Netznutzung',
    },
    {
      value: BatteryStorageUsage.Arbitrage,
      label: 'Marktdienlicher Einsatz / Arbitrage',
    },
  ]

  protected readonly formGroup: FormGroup<GeneralFormControls> = this.offerFormService.generalData

  private readonly HOURS_PER_YEAR = 8760
  private readonly TOTAL_CAPACITY_VALIDATORS = [Validators.required, Validators.max(10000)]

  private selectedBatteryStorageUsages: BatteryStorageUsage[] = []
  private triggerConsumptionPercentUpdate = true

  protected readonly useBatteryStorage: Signal<boolean> = computed(
    () =>
      this.batteryStorageStatus() == BatteryStorageStatus.Planned ||
      this.batteryStorageStatus() == BatteryStorageStatus.Exists
  )

  private readonly productionTechnology: Signal<ProductionTechnology | null> = toSignal(
    this.formGroup.controls.productionTechnology.valueChanges,
    { initialValue: this.formGroup.controls.productionTechnology.value }
  )

  private readonly technicalResources = toSignal(this.formGroup.controls.technicalResources.valueChanges, {
    initialValue: this.formGroup.controls.technicalResources.value,
  })

  private readonly plannedProductionPerYear: Signal<number | null> = toSignal(
    this.formGroup.controls.plannedProductionPerYear.valueChanges,
    { initialValue: this.formGroup.controls.plannedProductionPerYear.value }
  )

  private readonly ownConsumptionKw: Signal<number | null> = toSignal(
    this.formGroup.controls.ownConsumptionKw.valueChanges,
    {
      initialValue: this.formGroup.controls.ownConsumptionKw.value,
    }
  )

  private readonly batteryStorageStatus = toSignal(this.formGroup.controls.batteryStorageStatus.valueChanges, {
    initialValue: this.formGroup.controls.batteryStorageStatus.value,
  })

  private readonly totalInstalledCapacity: Signal<number | null> = computed(() => {
    return this.technicalResources()
      .map((resource) => {
        const capacity = MathService.parseNumber(resource.installedCapacityKW || 0)
        return Math.round((capacity + Number.EPSILON) * 1000) / 1000
      })
      .reduce((total, capacity) => total + capacity, 0)
  })

  constructor() {
    effect(() => {
      this.productionTechnology()
      this.setInitialPlannedProductionPerYear()
      this.updateOwnConsumptionValues()
      this.setTotalInstalledCapacityValidator()
    })

    effect(() => {
      this.formGroup.controls.totalInstalledCapacity.setValue(this.totalInstalledCapacity())
      this.setInitialPlannedProductionPerYear()
      this.validatePlannedProduction()
    })

    effect(() => {
      this.plannedProductionPerYear()
      this.validatePlannedProduction()
      this.validateOwnConsumption()
      this.updateOwnConsumptionValues()
    })

    effect(() => {
      this.ownConsumptionKw()
      this.validateOwnConsumption()
    })

    effect(() => {
      if (!this.batteryStorageStatus() || this.batteryStorageStatus() == BatteryStorageStatus.NotPlanned) {
        this.formGroup.controls.batteryStorageUsages.setErrors(null)
        this.selectedBatteryStorageUsages = []
        this.formGroup.controls.batteryStorageUsages.setValue([])
      } else if (!this.formGroup.controls.batteryStorageUsages.value?.length) {
        this.formGroup.controls.batteryStorageUsages.setErrors({
          required: true,
        })
      }
    })

    // do not use signal() with self-dependent controls
    this.formGroup.controls.ownConsumptionKw.valueChanges
      .pipe(
        startWith(this.formGroup.controls.ownConsumptionKw.value),
        pairwise(),
        filter(([prev, curr]) => prev !== curr)
      )
      .subscribe(([, newConsumption]) => {
        if (this.triggerConsumptionPercentUpdate) {
          this.setConsumptionPercent(newConsumption || 0)
        } else {
          this.triggerConsumptionPercentUpdate = true
        }
        this.updateFeedIn()
      })

    this.formGroup.controls.ownConsumptionPercent.valueChanges
      .pipe(
        startWith(this.formGroup.controls.ownConsumptionPercent.value),
        pairwise(),
        filter(([prev, curr]) => prev !== curr)
      )
      .subscribe(([, newConsumption]) => {
        this.setConsumptionKw(newConsumption || 0)
      })
  }

  /**
   * While the user is typing, bricks-input passes the raw text (e.g. "5.150.00") to the control,
   * so parse it the same way the format-view directive does on focusout.
   */
  private getPlannedProduction(): number {
    const value: number | string | null = this.formGroup.controls.plannedProductionPerYear.value
    const plannedProduction = typeof value === 'string' ? ViewFormatterService.stringToNumberFormatter(value) : value
    return plannedProduction && Number.isFinite(plannedProduction) ? plannedProduction : 0
  }

  private updateFeedIn() {
    const feedIn = this.getPlannedProduction() - (this.formGroup.controls.ownConsumptionKw.value || 0)
    this.formGroup.controls.feedInPerYear.setValue(feedIn)
  }

  private validatePlannedProduction() {
    const plannedProduction = this.formGroup.controls.plannedProductionPerYear
    const totalInstalledCapacity = this.formGroup.controls.totalInstalledCapacity
    if (
      !!plannedProduction?.value &&
      !!totalInstalledCapacity?.value &&
      plannedProduction.value > totalInstalledCapacity.value * this.HOURS_PER_YEAR
    ) {
      plannedProduction.setErrors({ capacityExceeded: true })
    } else if (plannedProduction?.hasError('capacityExceeded')) {
      plannedProduction.setErrors({ capacityExceeded: null })
    }
    return null
  }

  private validateOwnConsumption() {
    const plannedProduction = this.formGroup.controls.plannedProductionPerYear
    const ownConsumptionKw = this.formGroup.controls.ownConsumptionKw
    if (
      !!plannedProduction?.value &&
      !!ownConsumptionKw?.value &&
      Number(ownConsumptionKw.value) > Number(plannedProduction.value)
    ) {
      ownConsumptionKw.setErrors({ consumptionKwExceeded: true })
    } else if (ownConsumptionKw?.hasError('consumptionKwExceeded')) {
      ownConsumptionKw.setErrors({ consumptionKwExceeded: null })
    }
    return null
  }

  onBatteryUsageChange(option: BatteryStorageUsage, checked: boolean): void {
    if (checked) {
      this.selectedBatteryStorageUsages.push(option)
    } else {
      this.selectedBatteryStorageUsages = this.selectedBatteryStorageUsages.filter((usage) => usage !== option)
    }
    this.formGroup.controls.batteryStorageUsages.setValue(this.selectedBatteryStorageUsages)

    this.formGroup.controls.batteryStorageUsages.setErrors(
      this.selectedBatteryStorageUsages.length ? null : { required: true }
    )
  }

  private setConsumptionPercent(ownConsumptionKw: number): void {
    const plannedProduction = this.formGroup.controls.plannedProductionPerYear.value || 0
    const newValue =
      plannedProduction === 0 ? 0 : this.calculateConsumptionInPercent(plannedProduction, ownConsumptionKw)
    this.formGroup.controls.ownConsumptionPercent.setValue(newValue)
  }

  private setConsumptionKw(ownConsumptionPercent: number): void {
    const plannedProduction = this.formGroup.controls.plannedProductionPerYear.value || 0

    // avoid update kw because of rounding
    const currentValueInPercent =
      this.formGroup.controls.ownConsumptionKw.value != null
        ? this.calculateConsumptionInPercent(plannedProduction, this.formGroup.controls.ownConsumptionKw.value)
        : null
    if (currentValueInPercent != ownConsumptionPercent) {
      const newValue = this.calculateConsumptionInKw(plannedProduction, ownConsumptionPercent)
      this.formGroup.controls.ownConsumptionKw.setValue(newValue)
    }
  }

  private calculateConsumptionInKw(plannedProduction: number, ownConsumptionPercent: number) {
    return Math.round((plannedProduction / 100) * ownConsumptionPercent)
  }

  private calculateConsumptionInPercent(plannedProduction: number, ownConsumptionKw: number) {
    return Math.round((ownConsumptionKw / plannedProduction) * 100)
  }

  private updateOwnConsumptionValues(): void {
    const plannedProduction = this.getPlannedProduction()
    const consumptionPercent = this.formGroup.controls.ownConsumptionPercent.value || 0
    const consumptionKw = this.formGroup.controls.ownConsumptionKw.value || 0

    const newConsumptionInKw = this.calculateConsumptionInKw(plannedProduction, consumptionPercent)
    if (newConsumptionInKw !== consumptionKw) {
      this.triggerConsumptionPercentUpdate = false
      this.formGroup.controls.ownConsumptionKw.setValue(newConsumptionInKw)
    }
  }

  private setInitialPlannedProductionPerYear(): void {
    const technology = this.formGroup.controls.productionTechnology.value
    const totalCapacity = MathService.parseNumber(this.formGroup.controls.totalInstalledCapacity.value || 0)
    if (!technology || !totalCapacity) {
      return
    }
    if (technology === ProductionTechnology.Solar) {
      this.formGroup.controls.plannedProductionPerYear.setValue(Math.round(totalCapacity * 1030))
    } else if (technology === ProductionTechnology.WindOnshore) {
      this.formGroup.controls.plannedProductionPerYear.setValue(Math.round(totalCapacity * 1890))
    }
  }

  openBatteryUsageHint() {
    this.dialog.open(BatteryUsageHintComponent, { width: '640px' })
  }

  getPlannedProductionPerYearError(control: FormControl): string {
    if (control.hasError('zeroIsNotAllowed')) {
      return CustomValidators.zeroIsNotAllowedErrorMessage
    } else if (control.hasError('capacityExceeded')) {
      return 'Die angegebene Erzeugungsmenge ist technisch nicht möglich'
    }
    return ''
  }

  getOwnConsumptionPercentError(control: FormControl): string {
    if (control.hasError('pattern')) {
      return CustomValidators.zeroIsNotAllowedErrorMessage
    } else if (control.hasError('min') || control.hasError('max')) {
      return CustomValidators.minMaxPercentageErrorMessage
    }
    return ''
  }

  getOwnConsumptionKwError(control: FormControl): string {
    if (control.hasError('pattern')) {
      return CustomValidators.zeroIsNotAllowedErrorMessage
    } else if (control.hasError('min') || control.hasError('max')) {
      return 'Die Eingabe darf nicht größer sein als die geplante Erzeugung pro Jahr.'
    }
    return ''
  }

  setTotalInstalledCapacityValidator() {
    if (this.productionTechnology() === ProductionTechnology.Solar) {
      this.formGroup.controls.totalInstalledCapacity.setValidators([
        ...this.TOTAL_CAPACITY_VALIDATORS,
        Validators.min(this.minTotalInstalledCapacityForSolar),
      ])
    } else if (this.productionTechnology() === ProductionTechnology.WindOnshore) {
      this.formGroup.controls.totalInstalledCapacity.setValidators([
        ...this.TOTAL_CAPACITY_VALIDATORS,
        Validators.min(1000.001),
      ])
    }
    this.formGroup.controls.totalInstalledCapacity.updateValueAndValidity()
  }

  protected readonly ViewFormatterService = ViewFormatterService
  protected readonly integerToStringFormatter = ViewFormatterService.integerToStringFormatter()
}
